import usePrivateAxios from "@/common/api/usePrivateAxios"
import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import TextField from "@/common/components/TextField"
import { useMutation } from "@tanstack/react-query"
import { ChevronsRight } from "lucide-react"
import { Link } from "react-router-dom"
import { GeneralDocumentValidationSchema, createSchema } from "../../schemas/create.schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { SubmitHandler, useForm, Controller } from "react-hook-form"
import { useState } from "react"
import Spinner from "@/common/components/Spinner"
import { FileUploader } from "@/common/components/FileUploader"
import { checkIsPdf } from "@/common/utils/fileValidator"
import SelectController from "@/common/components/SelectController"
import { options as kindOptions } from "@/features/generalDocuments/constants/kind.constant"
import { useGoBack } from "@/common/hooks/useGoBack";
import GoBackButton from "@/common/components/GoBackButton";
import { DatePicker } from "@/common/components/ui/datePicker"
import { format, parse } from "date-fns"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import useUserPermissions from "@/common/hooks/useUserPermissions"

const Create = () => {
  const goBack = useGoBack();
  const privateAxios = usePrivateAxios();
  const [file, setFile] = useState<Blob>();
  const [fileName, setFileName] = useState('');
  const [serverError, setServerError] = useState('');

  const { mutate, isPending } = useMutation({
    mutationKey: ['createGeneralDocument'],
    mutationFn: async (form: GeneralDocumentValidationSchema) => {
      const payload = {
        ...form,
        kind: form.kind.value,
        file: file
      }
      return privateAxios.post('/general_documents', {
        general_document: payload
      }, {
        headers: {
          'content-type': "multipart/form-data"
        }
      })
    },
    onSuccess: () => {
      goBack({ fallback: '/general-documents' });
    },
    onError: () => {
      setServerError('generic');
    }
  });

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasCreateGeneralDocumentPermission = !isUserPermissionsPending ? hasUserPermission('Admin::GeneralDocuments::Create') : true

  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<GeneralDocumentValidationSchema>({
    resolver: zodResolver(createSchema)
  })

  const onSubmit: SubmitHandler<GeneralDocumentValidationSchema> = (data) => mutate(data);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasCreateGeneralDocumentPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/general-documents" />
            <p className="text-white-400 font-normal">Documentos</p>
            <ChevronsRight color='#ffffffb3' />
            <h2>Novo documento</h2>
          </div>
          <div className="flex gap-4">
            <Link to='/general-documents'><Button variant='outline-error'>Cancelar</Button></Link>
            <Button type="submit" data-testid="create_document" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" >{isPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Confirmar'}</Button>
          </div>
        </div>
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {serverError === 'generic' && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
          <p className="text-lg font-bold">Novo documento</p>
          <div className="flex items-center gap-2">
            <TextField
              label="Nome do documento"
              markAsRequired
              data-testid="display_name_input"
              className="w-2/4"
              errorMessage={
                serverError === 'Validation failed: Display name has already been taken'
                ? 'Documento com mesmo nome já existe'
                : errors?.display_name?.message
                ? errors?.display_name?.message
                : undefined
              }
              {...register('display_name')}
            />
            <SelectController
              control={control}
              name="kind"
              markAsRequired
              label="Tipo de documento"
              dataTestid="kind_selector"
              className="w-1/4"
              errorMessage={ errors.kind?.message || undefined }
              options={kindOptions}
            />
            <Controller
              control={control}
              name="expiration_date"
              render={({ field }) => {
                const dateValue = field.value ? parse(field.value, 'dd/MM/yyyy', new Date()) : undefined;

                return (
                  <DatePicker
                    name="expiration_date"
                    label="Validade"
                    dataTestId="expiration_date_input"
                    date={dateValue}
                    setDate={((date: Date | undefined) => {
                      const dateString = date ? format(date, 'dd/MM/yyyy') : '';
                      field.onChange(dateString);
                    }) as React.Dispatch<React.SetStateAction<Date | undefined>>}
                    className="w-1/4"
                    variant="select"
                    errorMessage={errors.expiration_date?.message || undefined}
                  />
                );
              }}
            />
          </div>
          <FileUploader
            label=""
            onChange={(e)  => {
              const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
              if (!file || !checkIsPdf(file)) {
                return
              }
              setFileName(file.name)
              setFile(file)
            }}
            name="document"
            data-testid="file_input"
            accept="application/pdf"
            fileName={fileName ? fileName : undefined}
            currentFileUrl={file ? URL.createObjectURL(file) : undefined}
          />
        </Box>
      </form>
    </ErrorMessageWrapper>
  )
}

export default Create
